import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Avatar,
  Drawer,
  Divider,
  Grid,
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleIcon from '@mui/icons-material/PlayCircle'; // Vidéos
import GroupsIcon from '@mui/icons-material/Groups'; // Ateliers
import StarsIcon from '@mui/icons-material/Stars'; // Offres
import AppSearch from '../../components/base/AppSearch';
import AppPagination from '../../components/base/AppPagination';
import ConfirmDialog from '../../components/base/ConfirmDialog';
import { DndContext, closestCenter } from '@dnd-kit/core';

import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';

import { CSS } from '@dnd-kit/utilities';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const SortableRow = ({ row, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: row.id_subs,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      sx={{
        '&:hover': { bgcolor: '#F8F9FF' },
        transition: '0.2s',
      }}
    >
      <TableCell width={50}>
        <IconButton
          {...attributes}
          {...listeners}
          size="small"
          sx={{ cursor: 'grab' }}
        >
          <DragIndicatorIcon />
        </IconButton>
      </TableCell>

      {children}
    </TableRow>
  );
};

const Abonnements = () => {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [subscriptions, setSubscriptions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // États pour les modales et Drawer
  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [formData, setFormData] = useState({
    name_subs: '',
    price_subs: '',
    description_subs: '',
    status_subs: 'active',
    interval_subs: 'month', // Requis par l'API
    interval_count_subs: 1, // Requis par l'API
    expiration_date_subs: '', // Requis par l'API
    nbr_video: 0,
    nbr_workshops: 0,
    nbr_offre: 0,
    photo_subs: null, // Pour le fichier
  });
  const [imagePreview, setImagePreview] = useState(null);
  // Fonction pour fermer la modale et réinitialiser le formulaire
  const handleClose = () => {
    setOpenModal(false);
    setImagePreview(null);
    setFormData({
      name_subs: '',
      price_subs: '',
      description_subs: '',
      status_subs: 'active',
      interval_subs: 'month',
      interval_count_subs: 1,
      expiration_date_subs: '',
      nbr_video: 0,
      nbr_workshops: 0,
      nbr_offre: 0,
      photo_subs: null,
    });
  };

  // Fonction pour gérer la sélection de l'image
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, photo_subs: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };
  const apiUrl = process.env.REACT_APP_API_URL;
  const storageUrl = process.env.REACT_APP_STORAGE_URL;
  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/admin/subs/g/get/back`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscriptions(response.data.result || []);
    } catch (err) {
      toast.error('Erreur de chargement des abonnements');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenDrawer = (sub) => {
    setSelectedSub(sub);
    setDrawerOpen(true);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const data = new FormData();

      Object.keys(formData).forEach((key) => {
        if (key === 'photo_subs') {
          if (formData[key] instanceof File) {
            data.append(key, formData[key]);
          }
        } else if (formData[key] !== null && formData[key] !== undefined) {
          data.append(key, formData[key]);
        }
      });

      if (isEditMode) {
        data.append('_method', 'PATCH');
      }

      const url = isEditMode
        ? `${apiUrl}/admin/subs/u/update/${formData.id_subs}`
        : `${apiUrl}/admin/subs/c/create`;

      await axios.post(url, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(isEditMode ? 'Modifié avec succès' : 'Créé avec succès');
      handleClose();
      fetchData();
    } catch (error) {
      console.error("Détails de l'erreur:", error.response);

      // 1. Récupérer le message d'erreur de l'API
      // Souvent structuré comme error.response.data.message
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Une erreur est survenue lors de l'enregistrement";

      // 2. Optionnel : Gérer les erreurs de validation spécifiques (ex: Laravel validation)
      const validationErrors = error.response?.data?.errors;
      if (validationErrors) {
        // Affiche la première erreur de validation trouvée
        const firstError = Object.values(validationErrors)[0][0];
        toast.error(`${errorMessage}: ${firstError}`);
      } else {
        // Affiche juste le message global
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };
  // delete

  const [isDeleting, setIsDeleting] = useState(false); // Pour le loading du bouton

  // Fonction appelée quand on clique sur l'icône de la corbeille
  const openDeleteConfirm = (id) => {
    setItemToDelete(id);
    setDeleteDialogOpen(true);
  };
  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axios.delete(`${apiUrl}/admin/subs/d/delete/${itemToDelete}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Abonnement supprimé avec succès');
      fetchData(); // Rafraîchir la liste
    } catch (error) {
      console.error(error);
      toast.error('Impossible de supprimer cet abonnement.');
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const filteredData = subscriptions.filter((item) =>
    item.name_subs?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const currentItems = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = subscriptions.findIndex(
      (item) => item.id_subs === active.id
    );

    const newIndex = subscriptions.findIndex(
      (item) => item.id_subs === over.id
    );

    const updatedItems = arrayMove(subscriptions, oldIndex, newIndex);

    // update UI instantanément
    setSubscriptions(updatedItems);

    try {
      await axios.patch(
        `${apiUrl}/admin/subs/back/${active.id}/move`,
        {
          new_position: newIndex + 1,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success('Ordre mis à jour');

      fetchData();
    } catch (error) {
      toast.error('Erreur lors du déplacement');

      fetchData();
    }
  };
  // Pour les lignes de statistiques en bas (Revenu, Membres, etc.)
  const DetailRow = ({ label, value, color = '#1B2559', bold = false }) => (
    <Box
      sx={{ display: 'flex', justifyContent: 'space-between', py: 1, px: 1 }}
    >
      <Typography variant="body2" sx={{ color: '#A3AED0', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: color, fontWeight: bold ? 800 : 700 }}
      >
        {value}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 3, bgcolor: '#F4F7FE', minHeight: '100vh' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4 }}
      >
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1B2559' }}>
          Offres d'Abonnements
        </Typography>
        <Stack direction="row" spacing={2}>
          {/* Barre de recherche (Composant AppSearch) */}
          <AppSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Rechercher par nom d'abonnement..."
          />{' '}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setIsEditMode(false);
              setFormData({
                name_subs: '',
                price_subs: '',
                status_subs: 'active',
                description_subs: '',
                nbr_video: 0,
                nbr_workshops: 0,
              });
              setOpenModal(true);
            }}
            sx={{
              bgcolor: '#4318FF',
              borderRadius: '20px', // Plus arrondi pour l'effet "pilule"
              px: 8, // Réduit le padding (12 était trop large)
              py: 1.5, // Ajuste le padding vertical au lieu d'une hauteur fixe
              textTransform: 'none',
              fontSize: '14px', // Taille de police standard dashboard
              fontWeight: 600, // Pour plus de lisibilité
              whiteSpace: 'nowrap', // Force le texte sur une seule ligne
              boxShadow: '0px 4px 12px rgba(67, 24, 255, 0.2)', // Optionnel: petite ombre portée
              '&:hover': {
                bgcolor: '#3311CC',
                boxShadow: 'none',
              },
            }}
          >
            Ajouter un abonnement
          </Button>
        </Stack>
      </Stack>

      <Paper
        sx={{
          borderRadius: '24px',
          boxShadow: '0px 20px 50px rgba(112, 144, 176, 0.08)',
          overflow: 'hidden',
          border: '1px solid #F1F4F9',
        }}
      >
        {loading ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <CircularProgress strokeWidth={3} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#F4F7FE' }}>
                <TableRow>
                    <TableCell width={50}></TableCell>
                  <TableCell
                    sx={{
                      color: '#A3AED0',
                      fontWeight: '700',
                      fontSize: '12px',
                      pl: 3,
                    }}
                  >
                    OFFRE
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#A3AED0',
                      fontWeight: '700',
                      fontSize: '12px',
                    }}
                  >
                    PRIX & CYCLE
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#A3AED0',
                      fontWeight: '700',
                      fontSize: '12px',
                    }}
                  >
                    REVENU TOTAL
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#A3AED0',
                      fontWeight: '700',
                      fontSize: '12px',
                    }}
                  >
                    STATUT
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: '#A3AED0',
                      fontWeight: '700',
                      fontSize: '12px',
                      pr: 3,
                    }}
                  >
                    ACTIONS
                  </TableCell>
                </TableRow>
              </TableHead>
              <DndContext
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={subscriptions.map((item) => item.id_subs)}
                  strategy={verticalListSortingStrategy}
                >
                  <TableBody>
                    {currentItems.map((row) => (
                      <SortableRow key={row.id_subs} row={row}>
                        {/* Colonne Offre */}
                        <TableCell sx={{ pl: 3 }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Avatar
                              src={`${storageUrl}/${row.photo_subs}`}
                              variant="rounded"
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '14px',
                                boxShadow: '0px 4px 12px rgba(0,0,0,0.05)',
                              }}
                            />
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 800, color: '#1B2559' }}
                              >
                                {row.name_subs}
                              </Typography>
                              {/* Date de création ajoutée ici */}
                              <Typography
                                variant="caption"
                                sx={{ color: '#A3AED0', display: 'block' }}
                              >
                                Créé le{' '}
                                {new Date(row.created_at).toLocaleDateString(
                                  'fr-FR',
                                  {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                  }
                                )}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        {/* Colonne Prix avec Intervalle */}
                        <TableCell>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              color: '#1B2559',
                              fontSize: '15px',
                            }}
                          >
                            {Number(row.price_subs).toLocaleString('fr-FR')} €
                          </Typography>
                          <Chip
                            label={
                              row.interval_subs === 'month'
                                ? 'Mensuel'
                                : row.interval_subs === 'year'
                                  ? 'Annuel'
                                  : row.interval_subs === 'week'
                                    ? 'Hebdo'
                                    : row.interval_subs
                            }
                            size="small"
                            sx={{
                              height: '18px',
                              fontSize: '10px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              bgcolor: '#E2E8F0',
                              color: '#475467',
                              mt: 0.5,
                            }}
                          />
                        </TableCell>

                        {/* Colonne Revenu */}
                        <TableCell>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              color: '#05CD99',
                              fontSize: '15px',
                            }}
                          >
                            {Number(row.total_revenue_subs).toLocaleString(
                              'fr-FR'
                            )}{' '}
                            €
                          </Typography>
                        </TableCell>

                        {/* Colonne Statut */}
                        <TableCell>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '12px',
                              // On définit le fond selon le statut exact
                              bgcolor:
                                row.status_subs === 'active'
                                  ? '#E6FAF5' // Vert clair pour actif
                                  : row.status_subs === 'inactive'
                                    ? '#FFF5F5' // Rouge clair pour inactif
                                    : '#F4F7FE', // Bleu gris pour brouillon
                              border: `1px solid ${
                                row.status_subs === 'active'
                                  ? '#05CD9920'
                                  : row.status_subs === 'inactive'
                                    ? '#EE5D5020'
                                    : '#A3AED020'
                              }`,
                            }}
                          >
                            {/* Le petit point indicateur */}
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                mr: 1,
                                bgcolor:
                                  row.status_subs === 'active'
                                    ? '#05CD99'
                                    : row.status_subs === 'inactive'
                                      ? '#EE5D50'
                                      : '#A3AED0',
                                // Animation de pulsation uniquement pour le statut "En ligne"
                                animation:
                                  row.status_subs === 'active'
                                    ? 'pulse-green 2s infinite'
                                    : 'none',
                                '@keyframes pulse-green': {
                                  '0%': {
                                    boxShadow: '0 0 0 0 rgba(5, 205, 153, 0.7)',
                                  },
                                  '70%': {
                                    boxShadow: '0 0 0 6px rgba(5, 205, 153, 0)',
                                  },
                                  '100%': {
                                    boxShadow: '0 0 0 0 rgba(5, 205, 153, 0)',
                                  },
                                },
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: '12px',
                                fontWeight: 800,
                                textTransform: 'uppercase',
                                color:
                                  row.status_subs === 'active'
                                    ? '#05CD99'
                                    : row.status_subs === 'inactive'
                                      ? '#EE5D50'
                                      : '#A3AED0',
                              }}
                            >
                              {row.status_subs === 'active'
                                ? 'Actif'
                                : row.status_subs === 'inactive'
                                  ? 'Désactivé'
                                  : 'Brouillon'}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Colonne Actions */}
                        <TableCell align="right" sx={{ pr: 3 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                          >
                            <IconButton
                              onClick={() => handleOpenDrawer(row)}
                              sx={{
                                bgcolor: '#F4F7FE',
                                color: '#4318FF',
                                '&:hover': { bgcolor: '#E1E9F8' },
                              }}
                              size="small"
                            >
                              <VisibilityIcon fontSize="inherit" />
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                setIsEditMode(true);
                                setFormData(row);
                                setOpenModal(true);
                              }}
                              sx={{
                                bgcolor: '#F4F7FE',
                                color: '#4318FF',
                                '&:hover': { bgcolor: '#E1E9F8' },
                              }}
                              size="small"
                            >
                              <EditIcon fontSize="inherit" />
                            </IconButton>
                            <IconButton
                              onClick={() => openDeleteConfirm(row.id_subs)}
                            >
                              <DeleteIcon color="error" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </SortableRow>
                    ))}
                  </TableBody>
                </SortableContext>
              </DndContext>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {!loading && filteredData.length > 0 && (
          <AppPagination
            count={Math.ceil(filteredData.length / itemsPerPage)}
            page={currentPage}
            onChange={(e, v) => setCurrentPage(v)}
            totalItems={filteredData.length} // Nombre total d'éléments après filtrage
            itemsPerPage={itemsPerPage} // Fixé à 8 dans votre code
          />
        )}
      </Paper>
      {/* --- Modale de Confirmation de Suppression --- */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Confirmer la suppression"
        message="Êtes-vous sûr de vouloir supprimer cet abonnement ? Cette action est irréversible."
        loading={isDeleting}
      />
      {/* --- DRAWER DÉTAILS PROFESSIONNEL --- */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ zIndex: 3000 }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 500 },
            border: 'none',
            boxShadow: '-10px 0px 30px rgba(0,0,0,0.08)', // Ombre douce pour profondeur
          },
        }}
      >
        {selectedSub && (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#fff',
            }}
          >
            {/* Header - Plus raffiné */}
            <Box
              sx={{
                p: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #F1F4F9',
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: '#1B2559', lineHeight: 1 }}
                >
                  Détails de l'abonnement
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: '#A3AED0', fontWeight: 500 }}
                >
                  ID: #SUB-00{selectedSub.id_subs}
                </Typography>
              </Box>
              <IconButton
                onClick={() => setDrawerOpen(false)}
                sx={{ bgcolor: '#F4F7FE', '&:hover': { bgcolor: '#E1E9F8' } }}
              >
                <CloseIcon sx={{ fontSize: '1.2rem', color: '#1B2559' }} />
              </IconButton>
            </Box>

            <Box sx={{ p: 3, overflowY: 'auto', flexGrow: 1 }}>
              {/* Image avec Overlay Statut */}
              <Box sx={{ position: 'relative', mb: 4 }}>
                <Avatar
                  src={`${storageUrl}/${selectedSub.photo_subs}`}
                  variant="rounded"
                  sx={{
                    width: '100%',
                    height: 220,
                    borderRadius: '24px',
                    boxShadow: '0px 20px 40px rgba(112, 144, 176, 0.2)',
                  }}
                />
                <Chip
                  label={
                    selectedSub.status_subs === 'active' ? 'Actif' : 'Inactif'
                  }
                  sx={{
                    position: 'absolute',
                    top: 15,
                    right: 15,
                    bgcolor:
                      selectedSub.status_subs === 'active'
                        ? '#05CD99'
                        : '#EE5D50',
                    color: '#fff',
                    fontWeight: 700,
                    borderRadius: '8px',
                  }}
                />
              </Box>

              <Typography
                variant="h4"
                sx={{ fontWeight: 800, color: '#1B2559', mb: 1 }}
              >
                {selectedSub.name_subs}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mb: 4 }}>
                <Typography
                  variant="h5"
                  sx={{ color: '#4318FF', fontWeight: 800 }}
                >
                  {selectedSub.price_subs}€
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#A3AED0',
                    fontWeight: 500,
                    alignSelf: 'center',
                  }}
                >
                  / {selectedSub.interval_subs === 'month' ? 'mois' : 'an'}
                </Typography>
              </Stack>

              {/* Section Facturation - Plus Pro */}
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#A3AED0',
                  fontWeight: 700,
                  mb: 1.5,
                  letterSpacing: '0.05em',
                }}
              >
                CYCLE DE FACTURATION
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: '20px',
                  bgcolor: '#F8F9FF',
                  border: '1px solid #E0E5F2',
                  mb: 4,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2.5}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 50,
                      height: 50,
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      boxShadow: '0px 4px 10px rgba(0,0,0,0.03)',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: '#4318FF', fontWeight: 800 }}
                    >
                      {selectedSub.interval_count_subs}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700, color: '#1B2559' }}
                    >
                      Prélèvement{' '}
                      {selectedSub.interval_subs === 'month'
                        ? 'Mensuel'
                        : 'Annuel'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#707EAE' }}>
                      Un paiement tous les {selectedSub.interval_count_subs}{' '}
                      {selectedSub.interval_subs === 'month' ? 'mois' : 'an'}.
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* Section Stats - Grid plus aérée */}
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#A3AED0',
                  fontWeight: 700,
                  mb: 2.5,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                CONTENU DE L'ABONNEMENT
              </Typography>

              <Stack direction="row" spacing={2.5}>
                <StatBox
                  icon={<PlayCircleIcon />}
                  label="Vidéos"
                  value={selectedSub.nbr_video}
                  color="#4318FF"
                />
                <StatBox
                  icon={<GroupsIcon />}
                  label="Ateliers"
                  value={selectedSub.nbr_workshops}
                  color="#39B8FF"
                />
                <StatBox
                  icon={<StarsIcon />}
                  label="Offres"
                  value={selectedSub.nbr_offre}
                  color="#05CD99"
                />
              </Stack>

              <Divider sx={{ my: 4, borderStyle: 'dashed' }} />

              {/* Description */}
              {/* Section Description Mise en Valeur */}
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#A3AED0',
                  fontWeight: 700,
                  mb: 1.5,
                  letterSpacing: '0.05em',
                }}
              >
                DESCRIPTION DE L'OFFRE
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: '16px',
                  bgcolor: '#F4F7FE', // Fond bleuté très léger
                  borderLeft: '4px solid #4318FF', // Barre d'accentuation à gauche
                  position: 'relative',
                  mb: 4,
                  '&::before': {
                    content: '"“"',
                    position: 'absolute',
                    top: -10,
                    left: 10,
                    fontSize: '40px',
                    color: '#E0E5F2',
                    fontFamily: 'serif',
                    fontWeight: 'bold',
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#1B2559',
                    lineHeight: 1.8,
                    whiteSpace: 'pre-line', // Important pour tes \r\n
                    fontWeight: 500,
                    fontStyle: 'italic',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {selectedSub.description_subs}
                </Typography>
              </Paper>

              {/* Footer Data List */}
              <Box sx={{ bgcolor: '#F4F7FE', borderRadius: '20px', p: 2 }}>
                <DetailRow
                  label="Revenu Total"
                  value={`${selectedSub.total_revenue_subs} €`}
                  color="#05CD99"
                  bold
                />
                <DetailRow
                  label="Membres Actifs"
                  value={selectedSub.subscriber_count_subs}
                />
                <DetailRow
                  label="Prochaine Expiration"
                  value={selectedSub.expiration_date_subs}
                />
                <DetailRow
                  label="Créé le"
                  value={new Date(selectedSub.created_at)
                    .toLocaleString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                    .replace(':', 'h')}
                />
              </Box>
            </Box>

            {/* Actions */}
            <Box sx={{ p: 3, borderTop: '1px solid #F1F4F9' }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => setDrawerOpen(false)}
                sx={{
                  bgcolor: '#1B2559',
                  color: '#fff',
                  borderRadius: '16px',
                  py: 2,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': { bgcolor: '#0B1437' },
                }}
              >
                Fermer les détails
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* --- MODALE AJOUT / EDITION --- */}
      <Dialog
        open={openModal}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper" // Permet au contenu de défiler si l'écran est petit
        PaperProps={{
          sx: {
            borderRadius: '24px',
            maxHeight: '90vh', // Évite que la modale dépasse l'écran
            boxShadow: '0px 20px 40px rgba(0,0,0,0.1)',
          },
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography
                sx={{ fontWeight: 800, fontSize: '1.4rem', color: '#1B2559' }}
              >
                {isEditMode
                  ? "Modifier l'abonnement"
                  : 'Créer un nouvel abonnement'}
              </Typography>
              {/* Texte d'instruction ajouté ici */}
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  color: '#A3AED0',
                  fontWeight: 500,
                  mt: 0.5,
                }}
              >
                Veuillez remplir tous les champs obligatoires marqués d'une{' '}
                <span
                  style={{
                    color: '#EE5D50',
                    fontWeight: 'bold',
                    marginLeft: '4px',
                  }}
                >
                  étoile rouge *
                </span>
              </Typography>
            </Box>
            <IconButton onClick={handleClose} sx={{ bgcolor: '#F4F7FE' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Ligne 1 : Nom et Prix */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                required // Ajoute l'étoile rouge
                label="Nom de l'abonnement"
                variant="filled"
                fullWidth
                value={formData.name_subs}
                onChange={(e) =>
                  setFormData({ ...formData, name_subs: e.target.value })
                }
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    borderRadius: '12px',
                    backgroundColor: 'white !important',
                    border: '1px solid #E0E5F2',
                    '&:hover': { backgroundColor: 'white !important' },
                    '&.Mui-focused': {
                      backgroundColor: 'white !important',
                      border: '1px solid #4318FF',
                    },
                  },
                }}
              />
              <TextField
                required // Ajoute l'étoile rouge
                label="Prix (€)"
                type="number"
                variant="filled"
                sx={{ width: { md: '300px' } }}
                value={formData.price_subs}
                onChange={(e) =>
                  setFormData({ ...formData, price_subs: e.target.value })
                }
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    borderRadius: '12px',
                    backgroundColor: 'white !important',
                    border: '1px solid #E0E5F2',
                    '&:hover': { backgroundColor: 'white !important' },
                    '&.Mui-focused': {
                      backgroundColor: 'white !important',
                      border: '1px solid #4318FF',
                    },
                  },
                }}
              />
            </Stack>

            {/* Ligne 2 : Description, Date et Statut */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                required
                label="Description"
                multiline
                rows={2}
                variant="filled"
                fullWidth
                value={formData.description_subs}
                onChange={(e) =>
                  setFormData({ ...formData, description_subs: e.target.value })
                }
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    borderRadius: '12px',
                    backgroundColor: 'white !important',
                    border: '1px solid #E0E5F2',
                    '&:hover': { backgroundColor: 'white !important' },
                    '&.Mui-focused': {
                      backgroundColor: 'white !important',
                      border: '1px solid #4318FF',
                    },
                  },
                }}
              />
              <TextField
                required
                label="Date d'expiration"
                type="date"
                variant="filled"
                InputLabelProps={{ shrink: true }}
                sx={{ width: { md: '300px' } }}
                value={formData.expiration_date_subs}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    expiration_date_subs: e.target.value,
                  })
                }
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    borderRadius: '12px',
                    backgroundColor: 'white !important',
                    border: '1px solid #E0E5F2',
                    '&:hover': { backgroundColor: 'white !important' },
                    '&.Mui-focused': {
                      backgroundColor: 'white !important',
                      border: '1px solid #4318FF',
                    },
                  },
                }}
              />
              <TextField
                required
                select
                label="Statut de l'offre"
                variant="filled"
                fullWidth
                sx={{ width: { md: '300px' } }}
                SelectProps={{ native: true }}
                value={formData.status_subs}
                onChange={(e) =>
                  setFormData({ ...formData, status_subs: e.target.value })
                }
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    borderRadius: '12px',
                    backgroundColor: 'white !important',
                    border: '1px solid #E0E5F2',
                    '&:hover': { backgroundColor: 'white !important' },
                    '&.Mui-focused': {
                      backgroundColor: 'white !important',
                      border: '1px solid #4318FF',
                    },
                  },
                }}
              >
                <option value="active">🟢 Active </option>
                <option value="inactive">🔴 Inactive </option>
              </TextField>
            </Stack>

            {/* Section Quantités - Les TextFields ici reçoivent aussi 'required' */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: '#A3AED0',
                  fontWeight: 700,
                  ml: 1,
                  textTransform: 'uppercase',
                }}
              >
                Inclusions du plan (Quantités)
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={4}>
                  <TextField
                    required
                    label="Vidéos"
                    type="number"
                    fullWidth
                    variant="filled"
                    InputLabelProps={{ shrink: true }}
                    value={formData.nbr_video}
                    onFocus={(e) =>
                      formData.nbr_video === 0 &&
                      setFormData({ ...formData, nbr_video: '' })
                    }
                    onBlur={(e) =>
                      formData.nbr_video === '' &&
                      setFormData({ ...formData, nbr_video: 0 })
                    }
                    onChange={(e) =>
                      setFormData({ ...formData, nbr_video: e.target.value })
                    }
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        borderRadius: '12px',
                        backgroundColor: 'white !important',
                        border: '1px solid #E0E5F2',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    required
                    label="Ateliers"
                    type="number"
                    fullWidth
                    variant="filled"
                    InputLabelProps={{ shrink: true }}
                    value={formData.nbr_workshops}
                    onFocus={(e) =>
                      formData.nbr_workshops === 0 &&
                      setFormData({ ...formData, nbr_workshops: '' })
                    }
                    onBlur={(e) =>
                      formData.nbr_workshops === '' &&
                      setFormData({ ...formData, nbr_workshops: 0 })
                    }
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        nbr_workshops: e.target.value,
                      })
                    }
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        borderRadius: '12px',
                        backgroundColor: 'white !important',
                        border: '1px solid #E0E5F2',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    required
                    label="Offres"
                    type="number"
                    fullWidth
                    variant="filled"
                    InputLabelProps={{ shrink: true }}
                    value={formData.nbr_offre}
                    onFocus={(e) =>
                      formData.nbr_offre === 0 &&
                      setFormData({ ...formData, nbr_offre: '' })
                    }
                    onBlur={(e) =>
                      formData.nbr_offre === '' &&
                      setFormData({ ...formData, nbr_offre: 0 })
                    }
                    onChange={(e) =>
                      setFormData({ ...formData, nbr_offre: e.target.value })
                    }
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        borderRadius: '12px',
                        backgroundColor: 'white !important',
                        border: '1px solid #E0E5F2',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Section Image - Pas de modification ici (pas d'étoile rouge) */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: '#A3AED0',
                  fontWeight: 700,
                  ml: 1,
                  textTransform: 'uppercase',
                  mb: 1,
                  display: 'block',
                }}
              >
                Image de couverture
              </Typography>
              <Box
                component="label"
                sx={{
                  border: '2px dashed #E0E5F2',
                  borderRadius: '16px',
                  p: 2,
                  textAlign: 'center',
                  bgcolor: '#F4F7FE',
                  cursor: 'pointer',
                  display: 'block',
                  '&:hover': { bgcolor: '#EDF2FF', borderColor: '#4318FF' },
                }}
              >
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageChange}
                />
                {imagePreview ? (
                  <Box
                    component="img"
                    src={imagePreview}
                    sx={{ height: '120px', borderRadius: '8px', mx: 'auto' }}
                  />
                ) : (
                  <Stack alignItems="center">
                    <AddIcon sx={{ color: '#4318FF', fontSize: 32 }} />
                    <Typography sx={{ fontSize: '12px', color: '#A3AED0' }}>
                      PNG, JPG (Max 2MB)
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleClose}
            sx={{ color: '#A3AED0', fontWeight: 700 }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              bgcolor: '#4318FF',
              borderRadius: '12px',
              px: 4,
              py: 1,
              boxShadow: '0px 10px 20px rgba(67, 24, 255, 0.2)',
            }}
          >
            {isEditMode ? 'Mettre à jour' : 'Publier'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const StatBox = ({ icon, label, value, color }) => {
  // On crée une couleur de fond très claire basée sur la couleur principale
  const bgColor = `${color}10`; // Ajoute 10 en hexadécimal pour ~6% d'opacité

  return (
    <Box
      sx={{
        p: 2,
        flex: 1,
        textAlign: 'center',
        borderRadius: '20px',
        bgcolor: bgColor, // Couleur dynamique ici
        border: `1px solid ${color}20`, // Bordure légère assortie
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-5px)',
          bgcolor: bgColor.replace('10', '20'), // S'assombrit légèrement au survol
          boxShadow: `0px 10px 20px ${color}15`, // Ombre colorée subtile
        },
      }}
    >
      <Box
        sx={{
          color: color,
          display: 'flex',
          justifyContent: 'center',
          mb: 1,
          '& svg': { fontSize: '1.8rem' }, // Icônes un peu plus grandes
        }}
      >
        {icon}
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 800, color: '#1B2559' }}>
        {value}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: color,
          fontWeight: 700,
          textTransform: 'uppercase',
          fontSize: '0.65rem',
          letterSpacing: '0.05em',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export default Abonnements;
